import React, { useState } from "react";
import classes from "./style.module.scss";
import { isEmpty, isUndefined, merge } from "lodash";
import classNames from "classnames";

import convertUnit from "utils/convertUnit";
import { unitLabels } from "utils/fields";
import { Collapse } from "@mui/material";
import Tooltip from "components/Tooltip";
import InfoTooltip from "components/InfoTooltip";

import WallIcon from "assets/images/components/Wall.svg";
import ExpFloorIcon from "assets/images/components/ExpFloor.svg";
import CeilingIcon from "assets/images/components/Ceiling.svg";
import BasementIcon from "assets/images/components/Foundation-Basement.svg";
import CrawlspaceIcon from "assets/images/components/Foundation-Crawlspace.svg";
import SlabIcon from "assets/images/components/Foundation-Slab.svg";
import WindowIcon from "assets/images/components/Window.svg";
import DoorIcon from "assets/images/components/Door.svg";
import FloorHeaderIcon from "assets/images/components/FloorHeader.svg";
import ArrowIcon from "assets/images/icons/arrow-down.svg";

const NON_COMPONENT_LINE_ITEMS = [
    { key: "ducts", label: "Duct Losses/Gains" },
    { key: "pipes", label: "Pipe Losses" },
    { key: "airChange", label: "Air Change (Natural & Mechanical)" },
    { key: "internalGains", label: "Internal Gains" },
];

const formatPower = (value, displayUnits) =>
    parseFloat(
        convertUnit({
            value,
            type: "power",
            inputUnit: "W",
            outputUnit: displayUnits === "Metric" ? "W" : "BTU/h",
        })
    ).toFixed(2);

const FloorAreaPlaceholder = () => <div className={classNames(classes.tableCell, classes.floorAreaPlaceholder)} aria-hidden />;

const NonComponentRow = ({ rowKey, label, heatLoss = 0, heatGain = 0, displayUnits }) => (
    <div className={classNames(classes.componentRow, classes.tableRow, classes.nonComponentRow)}>
        <div className={classNames(classes.tableCell, classes.componentInfo)}>
            <div className={classes.componentTitleContainer}>
                <div className={classes.componentLabel}>
                    <p data-html data-tip={`<span>${label}</span>`} data-for={`${rowKey}-label`}>
                        {label}
                        <Tooltip id={`${rowKey}-label`} />
                    </p>
                </div>
            </div>
            <div className={classes.componentStat}></div>
            <div className={classes.componentStat}></div>
            <div className={classes.componentStat}></div>
        </div>
        <FloorAreaPlaceholder />
        <div className={classes.tableCell}>{formatPower(heatLoss, displayUnits)}</div>
        <div className={classes.tableCell}>{formatPower(heatGain, displayUnits)}</div>
        <div className={classes.tableCell}></div>
    </div>
);

const NonComponentDetailsList = ({ nonComponentDetails, displayUnits }) => {
    if (!nonComponentDetails) {
        return <div className={classes.emptyRoomNotice}>Re-run a simulation to generate full room breakdown</div>;
    }

    const { heatLoss: heatLossDetails = {}, heatGain: heatGainDetails = {} } = nonComponentDetails;

    const visibleRows = NON_COMPONENT_LINE_ITEMS.filter(({ key }) => {
        const loss = heatLossDetails[key] || 0;
        const gain = heatGainDetails[key] || 0;
        return loss > 0 || gain > 0;
    });

    if (isEmpty(visibleRows)) {
        return null;
    }

    return visibleRows.map(({ key, label }) => (
        <NonComponentRow
            key={key}
            rowKey={key}
            label={label}
            heatLoss={heatLossDetails[key] || 0}
            heatGain={heatGainDetails[key] || 0}
            displayUnits={displayUnits}
        />
    ));
};

const ComponentRow = ({ componentKey, componentData, displayUnits, section = "" }) => {
    const {
        type = "",
        label = "",
        area = 0,
        wallArea = 0,
        floorArea = 0,
        rsi = 0,
        wallRsi = 0,
        slabRsi = 0,
        shgc = 0,
        heatLoss = 0,
        heatGain = 0,
    } = componentData;

    const isFoundation = ["Walls", "Floors"].includes(section);

    // Notify users to regenerate results  if windows are missing the isOnDoor field
    const isDoorWindow = type === "window" ? componentData.isOnDoor : false;

    const componentIcon = {
        wall: WallIcon,
        expFloor: ExpFloorIcon,
        ceiling: CeilingIcon,
        basement: BasementIcon,
        crawlspaceFloorsAbove: CrawlspaceIcon,
        slab: SlabIcon,
        window: WindowIcon,
        door: DoorIcon,
        floorHeader: FloorHeaderIcon,
    };

    const areaDisplayUnit = displayUnits === "Metric" ? "m2" : "ft2";

    const componentArea = area || wallArea;
    const convertedArea =
        componentArea > 0
            ? parseFloat(
                  convertUnit({
                      value: componentArea,
                      type: "area",
                      inputUnit: "m2",
                      outputUnit: areaDisplayUnit,
                  })
              ).toFixed(2)
            : null;

    const componentRsi = parseFloat(rsi || wallRsi || slabRsi).toFixed(2);

    return (
        <div
            className={classNames(classes.componentRow, classes.tableRow, {
                [classes.isFoundation]: isFoundation,
                [classes.isDoorWindow]: isDoorWindow,
            })}
        >
            <div className={classNames(classes.tableCell, classes.componentInfo)}>
                <div className={classes.componentTitleContainer}>
                    <div className={classes.componentLabel}>
                        <img src={componentIcon[type] || ""} alt={`${type}`} />
                        <p data-html data-tip={`<span>${label}</span>`} data-for={`${componentKey}-label`}>
                            {label}
                            <Tooltip id={`${componentKey}-label`} />
                        </p>
                        {isUndefined(isDoorWindow) && (
                            <span>
                                <InfoTooltip
                                    info="Re-generate results to ensure windows are grouped properly."
                                    className={classes.windowWarning}
                                />
                            </span>
                        )}
                    </div>
                    {isFoundation && <div className={classes.sectionType}>{section}</div>}
                </div>
                <div
                    className={classes.componentStat}
                    data-html
                    data-tip={`<span">Area: ${convertedArea || "&mdash;"}${unitLabels(areaDisplayUnit)}</span>`}
                    data-for={`${componentKey}-area`}
                >
                    <strong>A:</strong>
                    {convertedArea || <p>&mdash;</p>}
                    {unitLabels(areaDisplayUnit)}
                    <Tooltip id={`${componentKey}-area`} />
                </div>
                <div
                    className={classes.componentStat}
                    data-html
                    data-tip={`<span">R-Value: ${componentRsi}RSI</span>`}
                    data-for={`${componentKey}-r-value`}
                >
                    <strong>R:</strong>
                    {componentRsi}
                    RSI
                    <Tooltip id={`${componentKey}-r-value`} />
                </div>
                {type === "window" ? (
                    <div
                        className={classes.componentStat}
                        data-html
                        data-tip={`<span">Solar Heat Gain Coefficient: ${shgc}</span>`}
                        data-for={`${componentKey}-shgc`}
                    >
                        <strong>SHGC:</strong>
                        {shgc}
                        <Tooltip id={`${componentKey}-shgc`} />
                    </div>
                ) : (
                    <div className={classes.componentStat}></div>
                )}
            </div>
            <FloorAreaPlaceholder />
            <div className={classes.tableCell}>{formatPower(heatLoss, displayUnits)}</div>
            <div className={classes.tableCell}>{formatPower(heatGain, displayUnits)}</div>
            <div className={classes.tableCell}></div>
        </div>
    );
};

const FoundationFloorRow = ({ componentKey, componentData, displayUnits, section = "Floors" }) => {
    const { area = 0, floorArea = 0, rsi = 0, slabRsi = 0 } = componentData;

    const areaDisplayUnit = displayUnits === "Metric" ? "m2" : "ft2";

    const slabArea = area || floorArea;
    const convertedArea =
        slabArea > 0
            ? parseFloat(
                  convertUnit({
                      value: slabArea,
                      type: "area",
                      inputUnit: "m2",
                      outputUnit: areaDisplayUnit,
                  })
              ).toFixed(2)
            : null;

    const componentRsi = parseFloat(slabRsi || rsi).toFixed(2);

    return (
        <div className={classNames(classes.componentRow, classes.tableRow, classes.isFoundation, classes.isSecondRow)}>
            <div className={classNames(classes.tableCell, classes.componentInfo)}>
                <div className={classes.componentTitleContainer}>
                    <div className={classes.componentLabel}></div>
                    <div className={classes.sectionType}>
                        <p>{section}</p>
                    </div>
                </div>
                <div
                    className={classes.componentStat}
                    data-html
                    data-tip={`<span">Area: ${convertedArea || "&mdash;"}${unitLabels(areaDisplayUnit)}</span>`}
                    data-for={`${componentKey}-area`}
                >
                    <strong>A:</strong>
                    {convertedArea || <p>&mdash;</p>}
                    {unitLabels(areaDisplayUnit)}
                    <Tooltip id={`${componentKey}-area`} />
                </div>
                <div
                    className={classes.componentStat}
                    data-html
                    data-tip={`<span">R-Value: ${componentRsi}RSI</span>`}
                    data-for={`${componentKey}-r-value`}
                >
                    <strong>R:</strong>
                    {componentRsi}
                    RSI
                    <Tooltip id={`${componentKey}-r-value`} />
                </div>
                <div className={classes.componentStat}></div>
            </div>
            <FloorAreaPlaceholder />
            <div className={classes.tableCell}></div>
            <div className={classes.tableCell}></div>
            <div className={classes.tableCell}></div>
        </div>
    );
};

const SubComponentsList = ({ subComponentList, displayUnits }) => (
    <div className={classes.subComponentList}>
        {subComponentList.map(([subComponentKey, subComponentData]) => {
            const windows = subComponentData.windows || {};

            return (
                <div key={subComponentKey} className={classNames(classes.isSubComponent)}>
                    <ComponentRow
                        componentKey={subComponentKey}
                        componentData={subComponentData}
                        displayUnits={displayUnits}
                    />
                    {!isEmpty(windows) && (
                        <div className={classes.doorWindowList}>
                            {Object.entries(windows).map(([doorWindowKey, doorWindowData]) => (
                                <ComponentRow
                                    componentKey={doorWindowKey}
                                    componentData={doorWindowData}
                                    displayUnits={displayUnits}
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        })}
    </div>
);

const ComponentList = ({ componentsInRoom = {}, nonComponentDetails, displayUnits }) => {
    const mainComponents = Object.entries(componentsInRoom).filter(
        ([componentKey, { type }]) => !["floorHeader", "window", "door"].includes(type)
    );
    const subComponents = Object.entries(componentsInRoom)
        .filter(([componentKey, { type }]) => ["floorHeader", "window", "door"].includes(type))
        .reduce((acc, [componentKey, componentData]) => {
            const [parentKey, childKey, subChildKey] = componentKey.split("_");

            const childComponentData = subChildKey
                ? {
                      ...(acc[parentKey]?.[childKey] || {}),
                      ["windows"]: {
                          ...(acc[parentKey]?.[childKey]?.["windows"] || {}),
                          [subChildKey]: componentData,
                      },
                  }
                : componentData;

            return {
                ...acc,
                [parentKey]: {
                    ...acc[parentKey],
                    [childKey]: {
                        ...childComponentData,
                    },
                },
            };
        }, {});

    return (
        <div className={classNames(classes.roomBody)}>
            {isEmpty(mainComponents) && <div className={classes.emptyRoomNotice}>No components to display.</div>}
            {mainComponents.map(([componentKey, componentData]) => {
                const subComponentList = Object.entries(subComponents[componentKey] || {});

                if (["basement", "crawlspaceFloorsAbove"].includes(componentData.type)) {
                    return (
                        <React.Fragment key={componentKey}>
                            <ComponentRow
                                componentKey={componentKey}
                                componentData={componentData}
                                displayUnits={displayUnits}
                                section={"Walls"}
                            />
                            <FoundationFloorRow
                                componentKey={componentKey}
                                componentData={componentData}
                                displayUnits={displayUnits}
                                section={"Floors"}
                            />
                            {!isEmpty(subComponentList) && (
                                <SubComponentsList subComponentList={subComponentList} displayUnits={displayUnits} />
                            )}
                        </React.Fragment>
                    );
                }

                if (["slab"].includes(componentData.type)) {
                    return (
                        <ComponentRow
                            key={componentKey}
                            componentKey={componentKey}
                            componentData={componentData}
                            displayUnits={displayUnits}
                            section={"Floors"}
                        />
                    );
                }

                return (
                    <React.Fragment key={componentKey}>
                        <ComponentRow
                            componentKey={componentKey}
                            componentData={componentData}
                            displayUnits={displayUnits}
                        />
                        {!isEmpty(subComponentList) && (
                            <SubComponentsList subComponentList={subComponentList} displayUnits={displayUnits} />
                        )}
                    </React.Fragment>
                );
            })}
            <NonComponentDetailsList nonComponentDetails={nonComponentDetails} displayUnits={displayUnits} />
        </div>
    );
};

const StoreyRoom = ({ componentsInRoom = {}, roomData = {}, displayUnits, latentMultiplier }) => {
    const [expanded, setExpanded] = useState(false);
    const { name, area, heatLoss, heatGain, isDirectDucted, nonComponentDetails } = roomData;

    const roomLabel = `${name}${isDirectDucted ? " (Direct Ducted)" : ""}`;

    return (
        <div
            className={classNames(classes.roomContainer, {
                [classes.isExpanded]: expanded,
            })}
        >
            <div className={classNames(classes.roomHeader, classes.tableRow)} onClick={() => setExpanded(!expanded)}>
                <div className={classes.tableCell}>{roomLabel}</div>
                <div className={classes.tableCell}>
                    {parseFloat(
                        convertUnit({
                            value: area,
                            type: "area",
                            inputUnit: "m2",
                            outputUnit: displayUnits === "Metric" ? "m2" : "ft2",
                        })
                    ).toFixed(2)}
                </div>
                <div className={classes.tableCell}>
                    {parseFloat(
                        convertUnit({
                            value: heatLoss,
                            type: "power",
                            inputUnit: "W",
                            outputUnit: displayUnits === "Metric" ? "W" : "BTU/h",
                        })
                    ).toFixed(2)}
                </div>
                <div className={classes.tableCell}>
                    {(
                        latentMultiplier *
                        parseFloat(
                            convertUnit({
                                value: heatGain,
                                type: "power",
                                inputUnit: "W",
                                outputUnit: displayUnits === "Metric" ? "W" : "BTU/h",
                            })
                        )
                    ).toFixed(2)}
                </div>
                <div className={classNames(classes.tableCell, classes.expandButton)}>
                    <img src={ArrowIcon} alt="expand" />
                </div>
            </div>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <ComponentList
                    componentsInRoom={componentsInRoom}
                    nonComponentDetails={nonComponentDetails}
                    displayUnits={displayUnits}
                />
            </Collapse>
        </div>
    );
};

const StoreyFooter = ({ totalStoreySummary = {}, displayUnits, latentMultiplier }) => {
    return (
        <div className={classNames(classes.storeyFooter, classes.tableRow)}>
            <div className={classes.tableCell}>{totalStoreySummary.name}</div>
            <div className={classes.tableCell}>
                {parseFloat(
                    convertUnit({
                        value: totalStoreySummary.area,
                        type: "area",
                        inputUnit: "m2",
                        outputUnit: displayUnits === "Metric" ? "m2" : "ft2",
                    })
                ).toFixed(2)}
            </div>
            <div className={classes.tableCell}>
                {parseFloat(
                    convertUnit({
                        value: totalStoreySummary.heatLoss,
                        type: "power",
                        inputUnit: "W",
                        outputUnit: displayUnits === "Metric" ? "W" : "BTU/h",
                    })
                ).toFixed(2)}
            </div>
            <div className={classes.tableCell}>
                {(
                    latentMultiplier *
                    parseFloat(
                        convertUnit({
                            value: totalStoreySummary.heatGain,
                            type: "power",
                            inputUnit: "W",
                            outputUnit: displayUnits === "Metric" ? "W" : "BTU/h",
                        })
                    )
                ).toFixed(2)}
            </div>
            <div className={classes.tableCell}></div>
        </div>
    );
};

const RoomByRoomResults = ({
    className = "",
    summaryComponents = {},
    roomByRoomResults = {},
    displayUnits = "Metric",
    storeyCategories = [],
    includeLatent = true,
}) => {
    const { totals = {}, ...restRooms } = roomByRoomResults;
    const storeysPresent = storeyCategories.filter(({ id }) => id !== "other");

    const latentMultiplier = includeLatent ? 1.3 : 1;

    const componentsSortedByRoom = Object.entries(summaryComponents).reduce((acc, [componentType, componentList]) => {
        const componentsOfType = Object.entries(componentList).reduce((acc, [componentKey, componentData]) => {
            return {
                ...acc,
                [componentData.category]: {
                    ...acc[componentData.category],
                    [componentData.roomId]: {
                        ...(acc[componentData.category]?.[componentData.roomId] || {}),
                        [componentKey]: {
                            ...componentData,
                            type: componentType,
                        },
                    },
                },
            };
        }, {});
        return merge(acc, componentsOfType);
    }, {});

    return (
        <div className={classNames(classes.tableContainer, className)}>
            <div className={classNames(classes.resultTableHeader, classes.tableRow)}>
                <div className={classes.tableCell}>Room Name</div>
                <div className={classes.tableCell}>Floor Area {displayUnits === "Metric" ? "(m²)" : "(ft²)"}</div>
                <div className={classes.tableCell}>Heat Loss {displayUnits === "Metric" ? "(W)" : "(Btu/h)"}</div>
                <div className={classes.tableCell}>Heat Gain {displayUnits === "Metric" ? "(W)" : "(Btu/h)"}</div>
                <div className={classes.tableCell}></div>
            </div>
            <div className={classes.resultTableBody}>
                {storeysPresent.map((storeyCat, ind) => {
                    const roomsInStorey = roomByRoomResults[storeyCat.id] || {};
                    const componentsInStorey = componentsSortedByRoom[storeyCat.id] || {};

                    const sortedRoomKeys = Object.keys(roomsInStorey)
                        .filter((roomKey) => !roomKey.includes("-default-"))
                        .sort((keyA, keyB) =>
                            (roomsInStorey[keyA]?.name || "").localeCompare(roomsInStorey[keyB]?.name || "")
                        );

                    const totalStoreySummary = sortedRoomKeys.reduce(
                        (acc, roomKey) => {
                            return {
                                name: "Storey Totals",
                                heatLoss: acc.heatLoss + roomsInStorey[roomKey]?.heatLoss || 0,
                                heatGain: acc.heatGain + roomsInStorey[roomKey]?.heatGain || 0, //latent multiplier applied in buildRows
                                area: acc.area + roomsInStorey[roomKey]?.area || 0,
                            };
                        },
                        { heatLoss: 0, heatGain: 0, area: 0 }
                    );

                    return (
                        <div className={classes.storeyContainer} key={`${storeyCat}-${ind}`}>
                            <div className={classes.storeyContentContainer}>
                                <div className={classes.storeyHeader}>{storeyCat.name.toUpperCase()}</div>
                                <div className={classes.storeyRoomList}>
                                    {sortedRoomKeys.map((roomKey) => (
                                        <StoreyRoom
                                            key={`room-by-room-results-${roomKey}`}
                                            componentsInRoom={componentsInStorey[roomKey]}
                                            roomData={roomsInStorey[roomKey]}
                                            displayUnits={displayUnits}
                                            latentMultiplier={latentMultiplier}
                                        />
                                    ))}
                                </div>
                            </div>
                            <StoreyFooter
                                totalStoreySummary={totalStoreySummary}
                                displayUnits={displayUnits}
                                latentMultiplier={latentMultiplier}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RoomByRoomResults;
